import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, AlertCircle, CheckCircle, Clock, FileVideo, Loader } from 'lucide-react';

const VideoUploadModal = ({ isOpen, onClose, onUploadSuccess, currentUser }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, processing, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [moderationStatus, setModerationStatus] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [fileSize, setFileSize] = useState(0);
  const fileInputRef = useRef(null);
  const videoPreviewRef = useRef(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setTitle('');
      setDescription('');
      setUploadProgress(0);
      setUploadStatus('idle');
      setErrorMessage('');
      setModerationStatus(null);
      setVideoPreview(null);
      setVideoDuration(0);
      setFileSize(0);
    }
  }, [isOpen]);

  // Validate file when selected
  useEffect(() => {
    if (selectedFile) {
      validateFile(selectedFile);
    }
  }, [selectedFile]);

  const validateFile = async (file) => {
    setErrorMessage('');
    
    // Check file type
    const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
    if (!validTypes.includes(file.type)) {
      setErrorMessage('Invalid file type. Please upload MP4, MOV, AVI, or WEBM.');
      setSelectedFile(null);
      return false;
    }

    // Check file size (100MB = 100 * 1024 * 1024)
    const maxSize = 100 * 1024 * 1024;
    setFileSize(file.size);
    if (file.size > maxSize) {
      setErrorMessage(`File size exceeds 100MB limit. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`);
      setSelectedFile(null);
      return false;
    }

    // Create video preview and check duration
    const videoUrl = URL.createObjectURL(file);
    setVideoPreview(videoUrl);

    // Check video duration
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      const duration = video.duration;
      setVideoDuration(duration);
      
      // Max 2 minutes (120 seconds)
      if (duration > 120) {
        setErrorMessage(`Video duration exceeds 2 minute limit. Your video is ${Math.floor(duration / 60)}:${Math.floor(duration % 60).toString().padStart(2, '0')} long.`);
        setSelectedFile(null);
        setVideoPreview(null);
        return false;
      }
    };
    
    video.src = videoUrl;
    return true;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !title.trim()) {
      setErrorMessage('Please provide a video file and title.');
      return;
    }

    // Check user_type
    const userTypes = Array.isArray(currentUser.user_type) 
      ? currentUser.user_type 
      : (typeof currentUser.user_type === 'string' ? [currentUser.user_type] : []);
    
    if (userTypes.length === 0 || (userTypes.length === 1 && userTypes[0] === 'regular')) {
      setErrorMessage('You must set your user type (DJ, Producer, or Streamer) in your profile to upload promo videos.');
      return;
    }

    setUploadStatus('uploading');
    setUploadProgress(0);
    setErrorMessage('');

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('title', title.trim());
    formData.append('description', description.trim());

    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('beatseek_token');
      
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          const response = JSON.parse(xhr.responseText);
          setUploadStatus('success');
          setModerationStatus(response.moderation_status);
          
          setTimeout(() => {
            onUploadSuccess(response);
            onClose();
          }, 2000);
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            // Handle Pydantic validation errors (array format)
            if (Array.isArray(errorData.detail)) {
              const errorMessages = errorData.detail.map(err => err.msg || err).join(', ');
              setErrorMessage(errorMessages || 'Upload failed. Please try again.');
            } else if (typeof errorData.detail === 'string') {
              setErrorMessage(errorData.detail);
            } else {
              setErrorMessage('Upload failed. Please try again.');
            }
          } catch (e) {
            setErrorMessage('Upload failed. Please try again.');
          }
          setUploadStatus('error');
        }
      });

      xhr.addEventListener('error', () => {
        setUploadStatus('error');
        setErrorMessage('Network error. Please check your connection and try again.');
      });

      xhr.addEventListener('loadstart', () => {
        setUploadStatus('uploading');
      });

      xhr.addEventListener('loadend', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          setUploadStatus('processing');
        }
      });

      xhr.open('POST', `${API}/api/videos/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('error');
      setErrorMessage('An unexpected error occurred. Please try again.');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-cyan-500/30 w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileVideo className="text-cyan-400" size={28} />
            Promo Video Upload
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            disabled={uploadStatus === 'uploading' || uploadStatus === 'processing'}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* File Upload Area */}
          {!selectedFile && uploadStatus === 'idle' && (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
                isDragging
                  ? 'border-cyan-400 bg-cyan-500/10'
                  : 'border-gray-600 hover:border-cyan-500/50 hover:bg-slate-700/30'
              }`}
            >
              <Upload className="mx-auto mb-4 text-cyan-400" size={48} />
              <p className="text-white font-medium mb-2">
                Drag & drop your video here, or click to browse
              </p>
              <p className="text-gray-400 text-sm">
                MP4, MOV, AVI, or WEBM • Max 100MB • Max 2 minutes
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4,video/quicktime,video/x-msvideo,video/webm"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}

          {/* Video Preview */}
          {selectedFile && videoPreview && uploadStatus === 'idle' && (
            <div className="space-y-4">
              <div className="bg-black rounded-xl overflow-hidden">
                <video
                  ref={videoPreviewRef}
                  src={videoPreview}
                  controls
                  className="w-full max-h-80"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="flex items-center justify-between text-sm">
                <div className="text-gray-300">
                  <span className="font-medium">{selectedFile.name}</span>
                </div>
                <div className="text-gray-400 flex items-center gap-4">
                  <span>{formatFileSize(fileSize)}</span>
                  <span>{formatDuration(videoDuration)}</span>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedFile(null);
                  setVideoPreview(null);
                }}
                className="text-red-400 hover:text-red-300 text-sm underline"
              >
                Remove video
              </button>
            </div>
          )}

          {/* Title Input */}
          <div>
            <label className="block text-white font-medium mb-2">
              Video Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., 'Friday Night Set at Club XYZ'"
              maxLength={100}
              className="w-full bg-slate-700 text-white border border-gray-600 rounded-lg px-4 py-3 focus:outline-none focus:border-cyan-400 transition-colors"
              disabled={uploadStatus === 'uploading' || uploadStatus === 'processing'}
            />
            <div className="text-gray-400 text-xs mt-1 text-right">
              {title.length}/100
            </div>
          </div>

          {/* Description Input */}
          <div>
            <label className="block text-white font-medium mb-2">
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tell viewers about your video..."
              rows={4}
              maxLength={500}
              className="w-full bg-slate-700 text-white border border-gray-600 rounded-lg px-4 py-3 focus:outline-none focus:border-cyan-400 transition-colors resize-none"
              disabled={uploadStatus === 'uploading' || uploadStatus === 'processing'}
            />
            <div className="text-gray-400 text-xs mt-1 text-right">
              {description.length}/500
            </div>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="text-red-400 flex-shrink-0" size={20} />
              <p className="text-red-200 text-sm">{errorMessage}</p>
            </div>
          )}

          {/* Upload Progress */}
          {(uploadStatus === 'uploading' || uploadStatus === 'processing') && (
            <div className="bg-cyan-500/10 border border-cyan-500/50 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Loader className="text-cyan-400 animate-spin" size={20} />
                <span className="text-cyan-200 font-medium">
                  {uploadStatus === 'uploading' && `Uploading... ${uploadProgress}%`}
                  {uploadStatus === 'processing' && 'Processing and moderating video...'}
                </span>
              </div>
              {uploadStatus === 'uploading' && (
                <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-cyan-400 to-blue-500 h-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Success Message */}
          {uploadStatus === 'success' && (
            <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle className="text-green-400 flex-shrink-0" size={20} />
              <div>
                <p className="text-green-200 font-medium mb-1">Video uploaded successfully!</p>
                {moderationStatus === 'approved' && (
                  <p className="text-green-300 text-sm">✓ Video approved and is now visible on your profile.</p>
                )}
                {moderationStatus === 'pending' && (
                  <p className="text-yellow-300 text-sm">⏳ Video is under review. You'll be notified once approved.</p>
                )}
                {moderationStatus === 'rejected' && (
                  <p className="text-red-300 text-sm">✗ Video was rejected during moderation. Please upload different content.</p>
                )}
              </div>
            </div>
          )}

          {/* Info Box */}
          {uploadStatus === 'idle' && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <p className="text-blue-200 text-sm flex items-start gap-2">
                <AlertCircle className="flex-shrink-0 mt-0.5" size={16} />
                <span>
                  Your video will be automatically moderated using AI to ensure it meets our community guidelines. 
                  Approved videos appear on your profile immediately.
                </span>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {uploadStatus === 'idle' && (
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-300 hover:text-white transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={!selectedFile || !title.trim()}
              className={`px-6 py-2 rounded-lg font-medium transition-all ${
                selectedFile && title.trim()
                  ? 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              Upload Video
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoUploadModal;
